import React, { useState } from 'react';
import clsx from 'clsx';
import { ChevronDownIcon, ChevronUpIcon, UserMinusIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import { ClientCombobox, CollaboratorCombobox } from '@/components/shared';
import type { ConfigUsuarioGlpi, SignificadoUsuarioGlpi, UsuarioGlpiNaoEncontrado } from '../../../../services';
import type { StatusCustodiaEquipamento } from '../../../../models';
import { DETALHES_SITUACAO_ATIVO, SITUACAO_POR_STATUS } from './ativos.definitions';
import { descreverSituacao } from './ativoHistorico.format';
import type { ContextoAtivos } from './ativos.utils';

/** Situações que um usuário-marcador costuma significar (o resto se ajusta item a item). */
const OPCOES_SIGNIFICADO: Array<{ status: string; label: string }> = [
  { status: 'devolvido_cliente', label: 'Devolvido ao Cliente' },
  { status: 'em_homologacao', label: 'Em homologação (no Cliente)' },
  { status: 'estoque_raro', label: 'Em estoque' },
  { status: 'em_manutencao', label: 'Em manutenção' },
  { status: 'inativo', label: 'Inativo' },
];

interface UsuariosGlpiPainelProps {
  usuarios: UsuarioGlpiNaoEncontrado[];
  /** Config atual por login (inclui o que foi salvo nesta tela). */
  configs: Map<string, ConfigUsuarioGlpi | null>;
  onSalvar: (usuario: UsuarioGlpiNaoEncontrado, significado: SignificadoUsuarioGlpi | null) => Promise<void>;
  /** Pessoa que não casou sozinha (nome/login diferente no Digital) → qual colaborador é. */
  onVincularColaborador: (usuario: UsuarioGlpiNaoEncontrado, colaborador: { id: string; name: string }) => Promise<void>;
  onDesfazer: (usuario: UsuarioGlpiNaoEncontrado) => Promise<void>;
  contexto: ContextoAtivos;
}

function descreverSignificado(s: SignificadoUsuarioGlpi | null | undefined, contexto: ContextoAtivos): string {
  if (!s?.statusCustodia) return 'sem significado (mantém o status do GLPI)';
  const situacao = descreverSituacao(s.statusCustodia as StatusCustodiaEquipamento, s.situacaoDetalhe ?? null);
  const cliente = s.clienteId ? contexto.nomeClientePorId.get(s.clienteId) : null;
  const fornecedor = s.statusCustodia === 'em_manutencao' ? s.fornecedorManutencao : null;
  return [situacao, cliente, fornecedor].filter(Boolean).join(' · ');
}

/**
 * Quadro "Usuários do GLPI não encontrados nos colaboradores": o SI marca os que são
 * marcadores (ex.: "DEVOLVIDO PINE", "DEVOLVIDO SWAP") como "não é uma pessoa" e,
 * opcionalmente, diz o que significam; e, pras pessoas que não casaram sozinhas (nome/login
 * diferente no Digital, ex.: "Marcos Rodrigo Sol Souza" = "Rodrigo Sol"), diz qual
 * colaborador é. As duas marcações valem na hora pra todos os ativos daquele usuário e ficam
 * lembradas nas próximas importações (si_config_usuarios_glpi). Ex-colaborador sem cadastro
 * fica como está (sem colaborador).
 */
export default function UsuariosGlpiPainel({
  usuarios,
  configs,
  onSalvar,
  onVincularColaborador,
  onDesfazer,
  contexto,
}: UsuariosGlpiPainelProps) {
  const [aberto, setAberto] = useState(true);
  const [editando, setEditando] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [detalhe, setDetalhe] = useState('');
  const [clienteId, setClienteId] = useState('');
  const [fornecedor, setFornecedor] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [vinculando, setVinculando] = useState<string | null>(null);
  const [colaborador, setColaborador] = useState<{ id: string; name: string } | null>(null);

  if (usuarios.length === 0) return null;
  const pendentes = usuarios.filter((u) => {
    const c = configs.get(u.login);
    return !c?.naoEPessoa && !c?.colaboradorId;
  }).length;

  const comecar = (u: UsuarioGlpiNaoEncontrado) => {
    const config = configs.get(u.login);
    const base = config?.naoEPessoa ? config : u.sugestao;
    setVinculando(null);
    setEditando(u.login);
    setStatus(base?.statusCustodia ?? '');
    setDetalhe(base?.situacaoDetalhe ?? '');
    setClienteId(base?.clienteId ?? '');
    setFornecedor(base?.fornecedorManutencao ?? '');
  };

  const salvar = async (u: UsuarioGlpiNaoEncontrado) => {
    setSalvando(true);
    try {
      await onSalvar(
        u,
        status
          ? {
              statusCustodia: status,
              situacaoDetalhe: detalhe || null,
              clienteId: clienteId || null,
              fornecedorManutencao: status === 'em_manutencao' ? fornecedor.trim() || null : null,
            }
          : null
      );
      setEditando(null);
    } finally {
      setSalvando(false);
    }
  };

  const comecarVinculo = (u: UsuarioGlpiNaoEncontrado) => {
    const config = configs.get(u.login);
    setEditando(null);
    setVinculando(u.login);
    setColaborador(config?.colaboradorId ? { id: config.colaboradorId, name: config.colaboradorNome ?? '' } : null);
  };

  const salvarVinculo = async (u: UsuarioGlpiNaoEncontrado) => {
    if (!colaborador) return;
    setSalvando(true);
    try {
      await onVincularColaborador(u, colaborador);
      setVinculando(null);
    } finally {
      setSalvando(false);
    }
  };

  const situacao = status ? SITUACAO_POR_STATUS[status as StatusCustodiaEquipamento] : undefined;
  const detalhes = situacao ? DETALHES_SITUACAO_ATIVO[situacao] : undefined;

  return (
    <section className="rounded-md border border-amber-300 bg-amber-50">
      <button type="button" onClick={() => setAberto((a) => !a)} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm">
        <span className="font-semibold text-amber-900">
          Usuários do GLPI não encontrados nos colaboradores ({usuarios.length}){pendentes > 0 ? ` — ${pendentes} a revisar` : ''}
        </span>
        {aberto ? <ChevronUpIcon className="h-4 w-4 text-amber-700" /> : <ChevronDownIcon className="h-4 w-4 text-amber-700" />}
      </button>
      {aberto && (
        <ul className="divide-y divide-amber-200 border-t border-amber-200 bg-white">
          {usuarios.map((u) => {
            const config = configs.get(u.login);
            const marcado = Boolean(config?.naoEPessoa);
            const vinculado = Boolean(config?.colaboradorId) && !marcado;
            return (
              <li key={u.login} className="px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-gray-900">{u.nome}</span>
                  <span className="text-xs text-gray-400">({u.login})</span>
                  <span className="rounded-full bg-gray-100 px-2 text-xs text-gray-600">{u.quantidade} ativo(s)</span>
                  {marcado ? (
                    <span className="text-xs text-green-700">Não é uma pessoa · {descreverSignificado(config, contexto)}</span>
                  ) : vinculado ? (
                    <span className="text-xs text-green-700">É o colaborador {config?.colaboradorNome}</span>
                  ) : (
                    u.sugestao && <span className="text-xs text-amber-700">Sugestão: {descreverSignificado(u.sugestao, contexto)}</span>
                  )}
                  <span className="ml-auto flex gap-3 text-xs">
                    {!marcado && (
                      <button type="button" className="flex items-center gap-1 font-medium text-blue-600 hover:underline" onClick={() => comecarVinculo(u)}>
                        <UserPlusIcon className="h-3.5 w-3.5" />
                        {vinculado ? 'Trocar colaborador' : 'É um colaborador'}
                      </button>
                    )}
                    {!vinculado && (
                      <button type="button" className="flex items-center gap-1 font-medium text-blue-600 hover:underline" onClick={() => comecar(u)}>
                        <UserMinusIcon className="h-3.5 w-3.5" />
                        {marcado ? 'Editar' : 'Não é uma pessoa'}
                      </button>
                    )}
                    {(marcado || vinculado) && (
                      <button type="button" className="text-gray-500 hover:underline" onClick={() => onDesfazer(u)}>
                        Desfazer
                      </button>
                    )}
                  </span>
                </div>
                {vinculando === u.login && (
                  <div className="mt-2 grid gap-2 rounded bg-gray-50 p-2 sm:grid-cols-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-medium text-gray-600">Qual colaborador é</label>
                      <div className="mt-1">
                        <CollaboratorCombobox
                          selectedId={colaborador?.id ?? null}
                          onChange={(c) => setColaborador(c ? { id: c.id, name: c.name } : null)}
                          placeholder="Buscar colaborador..."
                        />
                      </div>
                    </div>
                    <div className="flex items-end justify-end gap-2">
                      <button type="button" className="btn-secondary text-sm" onClick={() => setVinculando(null)}>
                        Cancelar
                      </button>
                      <button type="button" className="btn-primary text-sm" disabled={salvando || !colaborador} onClick={() => salvarVinculo(u)}>
                        {salvando ? 'Salvando...' : `Aplicar aos ${u.quantidade} ativo(s)`}
                      </button>
                    </div>
                  </div>
                )}
                {editando === u.login && (
                  <div className={clsx('mt-2 grid gap-2 rounded bg-gray-50 p-2 sm:grid-cols-3')}>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">O que significa</label>
                      <select className="input-primary mt-1 w-full" value={status} onChange={(e) => { setStatus(e.target.value); setDetalhe(''); }}>
                        <option value="">Nada — só não é pessoa (mantém o status do GLPI)</option>
                        {OPCOES_SIGNIFICADO.map((o) => (
                          <option key={o.status} value={o.status}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {detalhes && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600">Detalhe</label>
                        <select className="input-primary mt-1 w-full" value={detalhe} onChange={(e) => setDetalhe(e.target.value)}>
                          <option value="">—</option>
                          {Object.entries(detalhes).map(([v, l]) => (
                            <option key={v} value={v}>
                              {l}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {(status === 'devolvido_cliente' || status === 'em_homologacao') && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600">Cliente</label>
                        <div className="mt-1">
                          <ClientCombobox selectedId={clienteId || null} onChange={(c) => setClienteId(c?.id ?? '')} placeholder="Buscar cliente..." />
                        </div>
                      </div>
                    )}
                    {status === 'em_manutencao' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600">Fornecedor da manutenção *</label>
                        <input
                          type="text"
                          className="input-primary mt-1 w-full"
                          placeholder="Ex.: Oficina do SMART"
                          value={fornecedor}
                          onChange={(e) => setFornecedor(e.target.value)}
                        />
                      </div>
                    )}
                    <div className="flex items-end justify-end gap-2 sm:col-span-3">
                      <button type="button" className="btn-secondary text-sm" onClick={() => setEditando(null)}>
                        Cancelar
                      </button>
                      <button
                        type="button"
                        className="btn-primary text-sm"
                        disabled={salvando || (status === 'em_manutencao' && !fornecedor.trim())}
                        onClick={() => salvar(u)}
                      >
                        {salvando ? 'Salvando...' : `Aplicar aos ${u.quantidade} ativo(s)`}
                      </button>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
